import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAdminAuth } from "@/admin/AdminAuthContext";
import { adminApi } from "@/admin/AdminAuthContext";
import { Inbox, CheckCircle2, Users, Sparkles, ArrowRight, Loader2 } from "lucide-react";

export default function AdminDashboard() {
  const { user } = useAdminAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/prospects?limit=1");
        if (!cancelled) setStats(data.stats);
      } catch (e) {
        if (!cancelled) setStats({ total: 0, pending_validation: 0, ready_to_send: 0 });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cards = [
    {
      Icon: Inbox,
      label: "À valider",
      value: stats?.pending_validation,
      hint: "File d'attente du jour",
      to: "/admin/inbox",
    },
    {
      Icon: CheckCircle2,
      label: "Prêts à envoyer",
      value: stats?.ready_to_send,
      hint: "Validés",
      to: "/admin/ready",
    },
    {
      Icon: Users,
      label: "Prospects en base",
      value: stats?.total,
      hint: "Total cumulé",
      to: "/admin/prospects",
    },
  ];

  return (
    <div className="px-6 md:px-10 py-10 max-w-6xl" data-testid="admin-dashboard">
      <div className="text-xs tracking-[2px] text-white/40 font-semibold">— TABLEAU DE BORD</div>
      <h1 className="mt-2 font-outfit text-3xl md:text-4xl font-light tracking-tight">
        Bonjour, <span className="gradient-text font-medium">{user?.name || "Admin"}</span>.
      </h1>
      <p className="mt-2 text-white/55 text-sm">
        Bienvenue dans l&apos;outil de prospection EM Group. Cette interface se construit étape par étape.
      </p>

      <div className="mt-10 grid sm:grid-cols-3 gap-4">
        {cards.map(({ Icon, label, value, hint, to }) => (
          <Link
            key={label}
            to={to}
            data-testid={`dash-card-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className="group p-5 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-[#E52431]/30 transition block"
          >
            <div className="flex items-center justify-between">
              <div className="text-xs tracking-[2px] text-white/40 font-semibold uppercase">{label}</div>
              <Icon size={16} className="text-white/40 group-hover:text-[#E52431] transition" />
            </div>
            <div className="mt-3 font-outfit text-3xl font-light">
              {value === undefined ? (
                <Loader2 className="w-5 h-5 animate-spin text-white/30" />
              ) : (
                value
              )}
            </div>
            <div className="mt-1 text-xs text-white/40 flex items-center justify-between">
              <span>{hint}</span>
              <ArrowRight size={12} className="text-white/30 group-hover:text-white group-hover:translate-x-0.5 transition" />
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-10 p-6 rounded-2xl border border-white/10 bg-gradient-to-br from-[#E52431]/8 via-white/[0.02] to-[#1F2C6E]/12">
        <div className="flex items-start gap-3">
          <Sparkles size={18} className="text-[#E52431] mt-0.5" />
          <div>
            <div className="font-semibold text-white">Itération 2 — Import CSV + Réglages</div>
            <p className="text-white/65 text-sm mt-1">
              Tu peux dès maintenant télécharger le modèle CSV, importer ta première liste de prospects et configurer
              les paramètres de la routine quotidienne. Les prochaines étapes ajouteront le scoring auto, l&apos;IA et la file de validation.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to="/admin/prospects"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-[0.12em] font-semibold gradient-bg text-white hover:shadow-[0_0_30px_rgba(229,36,49,0.4)] transition"
              >
                Importer ma liste <ArrowRight size={12} />
              </Link>
              <Link
                to="/admin/settings"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-[0.12em] font-semibold bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 transition"
              >
                Configurer les réglages
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
